import React, { useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, Card, CardHeader } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import LaborRuleSetForm, { laborRuleSetPayload } from "./LaborRuleSetForm";

const LaborRulesCard = ({ laborRules }) => {
  const { presets, presetOptions, createPath } = laborRules;
  const [teamDefault, setTeamDefault] = useState(laborRules.teamDefault);
  const [venues, setVenues] = useState(laborRules.venues);
  const [drafts, setDrafts] = useState({});

  const saveRuleSet = async (ruleSet, payload, venueOwnershipId) => {
    const request = ruleSet.path
      ? client.patch(ruleSet.path, { labor_rule_set: payload })
      : client.post(createPath, { labor_rule_set: { ...payload, venue_ownership_id: venueOwnershipId } });
    try {
      const { data } = await request;
      toastSuccess("Labor rules saved.");
      return data;
    } catch (error) {
      toastError("Could not save labor rules.");
      throw error;
    }
  };

  const saveTeamDefault = async (payload) => {
    const saved = await saveRuleSet(teamDefault, payload, null);
    setTeamDefault(saved);
    return saved;
  };

  const saveVenue = (venue) => async (payload) => {
    const saved = await saveRuleSet(venue.ruleSet || {}, payload, venue.venueOwnershipId);
    setVenues((current) => current.map((item) => (item.venueOwnershipId === venue.venueOwnershipId ? { ...item, ruleSet: saved } : item)));
    setDrafts((current) => ({ ...current, [venue.venueOwnershipId]: undefined }));
    return saved;
  };

  const startOverride = (venue) =>
    setDrafts((current) => ({
      ...current,
      [venue.venueOwnershipId]: laborRuleSetPayload(teamDefault),
    }));

  const switchToTeamDefault = async (venue) => {
    if (!venue.ruleSet) {
      setDrafts((current) => ({ ...current, [venue.venueOwnershipId]: undefined }));
      return;
    }
    try {
      await client.delete(venue.ruleSet.path);
      setVenues((current) => current.map((item) => (item.venueOwnershipId === venue.venueOwnershipId ? { ...item, ruleSet: null } : item)));
      toastSuccess(`${venue.name} now uses the team default.`);
    } catch (error) {
      toastError("Could not switch to the team default.");
    }
  };

  return (
    <>
      <Card
        padding="md"
        style={{ marginBottom: 20 }}
        header={<CardHeader title="Labor rules · Team default" supportingText="Applies to every venue that doesn't have its own rules." />}
      >
        <LaborRuleSetForm
          idPrefix="labor-rules-team"
          ruleSet={teamDefault}
          presets={presets}
          presetOptions={presetOptions}
          budgetScope="Every venue without its own rules"
          onSave={saveTeamDefault}
          saveLabel="Save team default"
        />
      </Card>

      {venues.map((venue) => {
        const ruleSet = venue.ruleSet || drafts[venue.venueOwnershipId];
        return (
          <div key={venue.venueOwnershipId} id={`labor-rules-venue-${venue.venueOwnershipId}`}>
            <Card
              padding="md"
              style={{ marginBottom: 20 }}
              header={
                <CardHeader
                  title={`Labor rules · ${venue.name}`}
                  supportingText={venue.ruleSet ? "Overrides the team default for this venue." : ruleSet ? "Not saved yet. This venue uses the team default until you save." : "Uses the team default."}
                  actions={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Badge color={venue.ruleSet ? "brand" : "gray"}>{venue.ruleSet ? "Custom rules" : "Team default"}</Badge>
                      {!ruleSet && (
                        <Button variant="secondary" size="sm" onClick={() => startOverride(venue)}>
                          Set rules for this venue
                        </Button>
                      )}
                    </div>
                  }
                />
              }
            >
              {ruleSet && (
                <LaborRuleSetForm
                  idPrefix={`labor-rules-venue-${venue.venueOwnershipId}`}
                  ruleSet={ruleSet}
                  presets={presets}
                  presetOptions={presetOptions}
                  budgetScope={venue.name}
                  onSave={saveVenue(venue)}
                  saveLabel="Save venue rules"
                  secondaryAction={
                    <Button variant="secondary" onClick={() => switchToTeamDefault(venue)}>
                      Use team default
                    </Button>
                  }
                />
              )}
            </Card>
          </div>
        );
      })}
    </>
  );
};

const ruleSetShape = PropTypes.shape({
  id: PropTypes.number,
  path: PropTypes.string,
  preset: PropTypes.string,
});

LaborRulesCard.propTypes = {
  laborRules: PropTypes.shape({
    teamDefault: ruleSetShape.isRequired,
    venues: PropTypes.arrayOf(
      PropTypes.shape({
        venueOwnershipId: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        ruleSet: ruleSetShape,
      })
    ).isRequired,
    presets: PropTypes.object.isRequired,
    presetOptions: PropTypes.array.isRequired,
    createPath: PropTypes.string.isRequired,
    currency: PropTypes.string,
  }).isRequired,
};

export default LaborRulesCard;
